import React, { useMemo } from "react";
import { connect } from "react-redux";

import { useIntl } from "react-intl";

import { Alert } from "@mapmycustomers/ui";

import { RootState } from "@app/store/rootReducer";

import {
  getGeocodeOrgLimit,
  isGeocodeMmcLimitReached,
  isGeocodeOrgLimitReached,
} from "../../store/iam";

import styles from "./GeocodeLimitInfo.module.scss";

interface Props {
  mmcLimitReached: boolean;
  noLimitReachedTitle: string;
  orgLimit: number;
  orgLimitReached: boolean;
}

const GecodeLimitInfo: React.FC<Props> = ({
  mmcLimitReached,
  noLimitReachedTitle,
  orgLimit,
  orgLimitReached,
}) => {
  const intl = useIntl();

  const message = useMemo(() => {
    if (mmcLimitReached) {
      return intl.formatMessage({
        id: "gecodeLimitInfo.mmcLimitReached.message",
        defaultMessage: "Unable to map imported records due to heavy geocoding usage",
        description: "Geocode limit info - MMC limit reached message",
      });
    }
    if (orgLimitReached) {
      return intl.formatMessage({
        id: "gecodeLimitInfo.orgLimitReached.message",
        defaultMessage: "Your organization has reached its daily geocoding limit",
        description: "Geocode limit info - Org limit reached message",
      });
    }
    return noLimitReachedTitle;
  }, [intl, mmcLimitReached, noLimitReachedTitle, orgLimitReached]);

  const description = useMemo(() => {
    if (mmcLimitReached) {
      return intl.formatMessage({
        id: "gecodeLimitInfo.mmcLimitReached.description",
        defaultMessage:
          "We’re currently experiencing heavy geocoding usage and are unable to map any records created or modified by import. You may still import these records, but they may not immediately appear on the map. Any records not mapped will be queued for geocoding tomorrow.",
        description: "Geocode limit info - MMC limit reached description",
      });
    }
    if (orgLimitReached) {
      return intl.formatMessage(
        {
          id: "gecodeLimitInfo.orgLimitReached.description",
          defaultMessage:
            "Please note that your organization has a daily geocoding limit of {limit} records. If you attempt to create more mapped records than your daily limit, some records may not immediately appear on the map. Any records not mapped will be queued for geocoding once your daily limit resets.",
          description: "Geocode limit info - Org limit reached description",
        },
        { limit: intl.formatNumber(orgLimit) }
      );
    }
    return intl.formatMessage(
      {
        id: "gecodeLimitInfo.noLimitReached.description",
        defaultMessage:
          "Please note that your organization has a daily geocoding limit of {limit} records. If you attempt to create more mapped records than your daily limit, some records may not immediately appear on the map. Any records not geocoded will be queued for geocoding once your daily limit resets.",
        description: "Geocode limit info - No limit reached description",
      },
      {
        limit: intl.formatNumber(orgLimit),
      }
    );
  }, [intl, mmcLimitReached, orgLimitReached, orgLimit]);

  return (
    <Alert
      className={styles.alert}
      description={description}
      message={message}
      showIcon
      type={orgLimitReached || mmcLimitReached ? "warning" : "info"}
    />
  );
};

const mapStateToProps = (state: RootState) => ({
  mmcLimitReached: isGeocodeMmcLimitReached(state),
  orgLimit: getGeocodeOrgLimit(state),
  orgLimitReached: isGeocodeOrgLimitReached(state),
});

export default connect(mapStateToProps)(GecodeLimitInfo);
